import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, InfoWindowF } from "@react-google-maps/api";
import "../styles/style.css";
import locationContext from "../../app/providers/location/locationContext";
import workshopContext from "../../app/providers/workhsop/workshopContext";
import { useWindowSize } from "../hooks/useWindowSize";
import { Box, Button, Typography } from "@mui/material";
import Rating from "./Rating";
import { chooseButtonTurquoiseStyle } from "../styles/buttonStyles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { googleMapsLibraries, googleMapsApiKey } from "../configs/googleMaps";
import NumberLocation from "../../features/workshop/components/workshop_extras/NumberLocation";
import { createRoot } from "react-dom/client";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import env from "../../app/config/envHelper";
import { useDispatch, useSelector } from "react-redux";
import { defaultLocation, emptySelectedWorkshop, emptyWorkshopForMap, setWorkshopForMap, workshopForMap, workshops } from "../../app/store/workshopSlice";
import { useGetRatingsQuery, useGetWorkshopsQuery } from "../../app/store/api/workshopApi";
import filterContext from "../../app/providers/filter/filterContext";
import orderByContext from "../../app/providers/orderBy/orderByContext";
import carBrandContext from "../../app/providers/car-brands/carBrandContext";
import sortWorkshopsByOrder from "../../features/workshop/utils/sortWorkshopsByOrder";

const helsinki = { lat: 60.1695, lng: 24.9354 };

// Keep libraries static to prevent reload warnings
const LIBRARIES = [...googleMapsLibraries];

export default function Map() {
  const { state: locationState } = useContext(locationContext);
  const { state : filterState } = useContext(filterContext);
  const { state: orderByState } = useContext(orderByContext);
  const { state: carBrandState } = useContext(carBrandContext);
  const storedDefaultLocation = useSelector(defaultLocation);
  const draftCar = useSelector((state) => state.reservation.draft.car);
  const location = locationState.location || storedDefaultLocation;
  const filter = filterState.filter || [];
  const selectedCar = draftCar || carBrandState.brand;
  const {data:workshopsList} = useGetWorkshopsQuery({ location, filter, car: selectedCar });
  const sortedWorkshopsList = useMemo(
    () => sortWorkshopsByOrder(workshopsList, orderByState.orderBy),
    [workshopsList, orderByState.orderBy]
  );
  const [selectedWorkshopForMap, setSelectedWorkshopForMap] = useState(null);
  const {data:ratingsData} = useGetRatingsQuery({ workshopId: selectedWorkshopForMap?.id });
  const {ratings} = ratingsData || {};
  const dispatch = useDispatch();
  const storedWorkshops = useSelector(workshops);
  const storedSelectedWorkshopForMap = useSelector(workshopForMap);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useWindowSize();

  const [center, setCenter] = useState(helsinki);
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(10);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: LIBRARIES,
  });

  // Store markers to clean up later
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const clustererRef = useRef(null);

  useEffect(() => {
    if (locationState.location) {
      setCenter(locationState.location);
    } else {
      setCenter(helsinki);
    }
  }, [locationState.location]);

  useEffect(() => {
    setSelectedWorkshopForMap(storedSelectedWorkshopForMap);
  }, [storedWorkshops, storedSelectedWorkshopForMap]);

  const containerStyle = {
    width: "100%",
    height: isDesktop ? "calc(100vh - 64px)" : "calc(100vh - 120px)",
    minHeight: isDesktop ? "500px" : "300px",
  };

  // --- 1️⃣ User location marker effect
  useEffect(() => {
    if (!map || !window.google?.maps?.marker) return;

    const { AdvancedMarkerElement } = window.google.maps.marker;

    // Clear previous user marker
    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }

    if (locationState.location) {
      const userPinDiv = document.createElement("div");
      userPinDiv.innerHTML = `
        <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>
          <circle cx='16' cy='16' r='10' fill='#f63131ff' stroke='white' stroke-width='4'/>
        </svg>
      `;
      userMarkerRef.current = new AdvancedMarkerElement({
        map,
        position: locationState.location,
        title: "Your location",
        content: userPinDiv,
      });
    }
  }, [map, locationState.location]);

  // --- 2️⃣ Workshop markers effect
  useEffect(() => {
    if (!map || !window.google?.maps?.marker) return;

    const { AdvancedMarkerElement } = window.google.maps.marker;

    // Clean up previous markers & clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }

    markersRef.current.forEach(({ marker, root }) => {
      marker.setMap(null);
      root.unmount();
    });
    markersRef.current = [];

    // Create new markers
    const newMarkers = [];

    sortedWorkshopsList?.forEach((workshop) => {
      const markerDiv = document.createElement("div");
      const root = createRoot(markerDiv);
      root.render(<NumberLocation orderNr={workshop.orderNr} map />);

      const marker = new AdvancedMarkerElement({
        position: workshop.location,
        title: workshop.name,
        content: markerDiv,
      });

      marker.addListener("gmp-click", () => dispatch(setWorkshopForMap(workshop)));
      markersRef.current.push({ marker, root });
      newMarkers.push(marker);
    });

    // Initialize MarkerClusterer
    if (newMarkers.length > 0) {
      clustererRef.current = new MarkerClusterer({
        map,
        markers: newMarkers,
        renderer: {
          render: ({ count, position }) => {
            const clusterDiv = document.createElement("div");
            clusterDiv.className = "custom-map-cluster";
            clusterDiv.textContent = String(`${count-1}+`);

            return new AdvancedMarkerElement({
              position,
              content: clusterDiv,
              zIndex: 1000 + count,
            });
          }
        }
      });
    }

    // Cleanup when component unmounts
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
      markersRef.current.forEach(({ marker, root }) => {
        marker.setMap(null);
        // root.unmount();
      });
      markersRef.current = [];
    };
  }, [map, sortedWorkshopsList]);

  // Calculate latitude offset in degrees so InfoWindow is always 35px above the marker center
  const getLatitudeOffset = () => {
    if (!map) return 0.0015;
    const currentZoom = map.getZoom();
    const lat = selectedWorkshopForMap?.location?.lat || helsinki.lat;
    const cosLat = Math.cos((lat * Math.PI) / 180);
    const metersPerPixel = (156543.03392 * cosLat) / Math.pow(2, currentZoom);
    const degreesPerPixel = metersPerPixel / 111111;
    return 35 * degreesPerPixel;
  };

  if (!isLoaded) return <Box>Loading map...</Box>;

  return (
    <Box className="map">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        onLoad={(mapInstance) => {
          setMap(mapInstance);
          setZoom(mapInstance.getZoom());
        }}
        onZoomChanged={() => {
          if (map) {
            setZoom(map.getZoom());
          }
        }}
        options={{
          mapId: env.REACT_APP_MAP_ID,
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
        }}
      >
        {selectedWorkshopForMap && (
          <InfoWindowF
            position={{
              lat: selectedWorkshopForMap.location.lat + getLatitudeOffset(),
              lng: selectedWorkshopForMap.location.lng,
            }}
            onCloseClick={() => dispatch(emptyWorkshopForMap())}
          >
            <Box className="info-window">
              <Box
                className="info-window-header"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {selectedWorkshopForMap.name}
                  </Typography>
                  <Typography variant="body2" component="div">
                    {selectedWorkshopForMap.address}
                  </Typography>
                </Box>
              </Box>

              <Box
                className="info-window-ratings"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "16px",
                  width: "100%",
                }}
              >
                <Rating rating={ratings} />
              </Box>

              <Box
                className="info-window-actions"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "16px",
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  sx={{ ...chooseButtonTurquoiseStyle, width: "200px" }}
                  onClick={() =>
                    navigate(
                      `/workshops/${selectedWorkshopForMap.id}?step=1`
                    )
                  }
                >
                  {t("actions.choose")}
                </Button>
              </Box>
            </Box>
          </InfoWindowF>
        )}
      </GoogleMap>
    </Box>
  );
}
